<template>
  <div class="request-invoice">
    <NavHeader
      :title="$t('components.requestInvoice.title')"
      @onBackClick="goBack"
    />
    <LayoutCard rounded>
      <template v-if="!showSuccessScreen">
        <div class="tp-text-subtitle">
          {{ $t('components.requestInvoice.form.title') }}
        </div>
        <div class="tp-text-body mt-3">
          {{ $t('components.requestInvoice.form.subtitle') }}
        </div>

        <div class="tp-text-subtitle-semibold-black mt-6">
          {{ $t('components.requestInvoice.form.contactDetails') }}
        </div>
        <v-form ref="form" validate-on="submit" @submit.prevent="submitData">
          <v-text-field
            v-model="form.name"
            :disabled="form.loading"
            color="black"
            :rules="[() => !!form.name || $t('generic.requiredField')]"
            :label="$t('components.requestInvoice.form.namePlaceholder')"
            :placeholder="$t('components.requestInvoice.form.namePlaceholder')"
            type="text"
            class="py-2 mt-3"
            maxlength="100"
          ></v-text-field>
          <v-text-field
            v-model="form.email"
            :disabled="form.loading"
            color="black"
            :rules="[() => !!form.email || $t('generic.requiredField')]"
            :label="$t('components.requestInvoice.form.emailPlaceholder')"
            :placeholder="$t('components.requestInvoice.form.emailPlaceholder')"
            type="email"
            class="py-2"
            :hint="$t('components.requestInvoice.form.emailHint')"
            persistent-hint
            maxlength="100"
          ></v-text-field>
          <div class="tp-text-subtitle-semibold-black mt-6">
            {{ $t('components.requestInvoice.form.companyDetails') }}
          </div>
          <v-autocomplete
            v-model="form.country"
            :items="countries"
            item-text="title"
            item-value="value"
            :disabled="form.loading || dataFilled"
            color="black"
            item-color="black"
            :rules="[() => !!form.country || $t('generic.requiredField')]"
            :label="$t('components.requestInvoice.form.countryPlaceholder')"
            :placeholder="
              $t('components.requestInvoice.form.countryPlaceholder')
            "
            class="py-2 mt-3"
          />
          <SearchForCompany
            v-if="!showCompanyNameManually"
            :country-code="form.country"
            @onCompanySelected="fillCompanyData"
            @onCompanyNameManually="resetDataForManualEntry"
          />
          <v-expand-transition>
            <v-text-field
              v-if="showCompanyNameManually"
              v-model="form.businessName"
              :disabled="form.loading || dataFilled"
              color="black"
              :rules="[
                () => !!form.businessName || $t('generic.requiredField'),
              ]"
              :label="
                $t('components.requestInvoice.form.businessNamePlaceholder')
              "
              :placeholder="
                $t('components.requestInvoice.form.businessNamePlaceholder')
              "
              type="text"
              class="py-2"
              maxlength="100"
            >
              <template #append>
                <v-icon
                  v-if="dataFilled"
                  @click="resetDataForAutomaticEntry"
                  class="mr-2"
                  color="black"
                >
                  mdi-close-circle
                </v-icon>
              </template>
            </v-text-field>
          </v-expand-transition>
          <v-text-field
            v-model="form.businessCode"
            :disabled="form.loading || dataFilled"
            color="black"
            :rules="[() => !!form.businessCode || $t('generic.requiredField')]"
            :label="
              $t('components.requestInvoice.form.businessCodePlaceholder')
            "
            :placeholder="
              $t('components.requestInvoice.form.businessCodePlaceholder')
            "
            type="text"
            class="py-2"
            maxlength="100"
          ></v-text-field>
          <v-text-field
            v-model="form.businessAddress"
            :disabled="form.loading"
            color="black"
            :rules="[
              () => !!form.businessAddress || $t('generic.requiredField'),
            ]"
            :label="
              $t('components.requestInvoice.form.businessAddressPlaceholder')
            "
            :placeholder="
              $t('components.requestInvoice.form.businessAddressPlaceholder')
            "
            type="text"
            class="py-2"
            maxlength="100"
          ></v-text-field>
          <div
            class="d-flex align-center justify-space-between mb-3"
            style="gap: 2em"
          >
            <div class="tp-text-subtitle-semibold">
              {{ $t('components.requestInvoice.form.vatCodeQuestion') }}
            </div>
            <div class="d-flex align-center justify-flex-end">
              <v-switch
                v-model="form.enableVatCodeField"
                :disabled="form.loading || dataFilled"
                hide-details
                :ripple="false"
                color="success"
                inset
                dense
                class="marginless"
              ></v-switch>
            </div>
          </div>
          <v-text-field
            v-if="form.enableVatCodeField"
            v-model="form.vatCode"
            :disabled="form.loading || dataFilled"
            color="black"
            :rules="[
              () =>
                (form.enableVatCodeField && !!form.vatCode) ||
                $t('generic.requiredField'),
            ]"
            :label="$t('components.requestInvoice.form.vatCodePlaceholder')"
            :placeholder="
              $t('components.requestInvoice.form.vatCodePlaceholder')
            "
            type="text"
            class="py-3"
            maxlength="100"
          ></v-text-field>
          <v-checkbox
            v-model="form.saveRequestInvoiceDetails"
            class="mb-10"
            color="black"
            :ripple="false"
            hide-details
            :label="$t('components.requestInvoice.form.saveDetails')"
          />
          <div class="px-6 mb-3">
            <Button dark :disabled="form.loading">
              <v-progress-circular
                v-if="form.loading"
                indeterminate
                :size="15"
                :width="2"
              ></v-progress-circular>
              <span v-else>{{
                $t('components.requestInvoice.form.submitButton')
              }}</span>
            </Button>
          </div>

          <div
            v-if="error"
            class="text-center mt-3 tp-text-caption"
            :style="{ color: 'red' }"
          >
            {{ error }}
          </div>
        </v-form>
      </template>
      <template v-else>
        <div
          class="d-flex flex-column justify-space-between"
          :style="{ minHeight: `80vh` }"
        >
          <div></div>
          <div class="d-flex flex-column align-center justify-center">
            <img
              :src="require(`@/assets/images/icons/checkmark-success.svg`)"
              width="128"
            />
            <div class="tp-text-title-bold mt-8">
              {{ $t('components.requestInvoice.success.title') }}
            </div>
            <div class="tp-text-body text-center mt-3">
              {{ $t('components.requestInvoice.success.subtitle') }}
            </div>
          </div>
          <div class="px-6">
            <Button dark @click="goBack">
              <span>{{ $t('generic.back') }}</span>
            </Button>
          </div>
        </div>
      </template>
    </LayoutCard>
  </div>
</template>

<script>
import NavHeader from '@/components/Navigation/NavHeader.vue';
import LayoutCard from '@/components/LayoutCard.vue';
import Button from '@/components/Button.vue';
import { CountriesList } from '@/utils/constants';
import { parseQueryParam } from '@/utils/strings';
import SearchForCompany from '@/components/Invoices/SearchForCompany.vue';

export default {
  components: {
    NavHeader,
    LayoutCard,
    Button,
    SearchForCompany,
  },
  data() {
    return {
      form: {
        name: null,
        businessName: null,
        businessCode: null,
        businessAddress: null,
        email: null,
        country: 'LT',
        enableVatCodeField: false,
        vatCode: null,
        loading: false,
        saveRequestInvoiceDetails: false,
      },
      showSuccessScreen: false,
      error: null,
      enterCompanyNameManually: false,
      dataFilled: false,
    };
  },
  computed: {
    showCompanyNameManually() {
      return this.enterCompanyNameManually || this.form.country !== 'LT';
    },
    countries() {
      const countries = CountriesList;

      return Object.entries(countries).map(([code, name]) => {
        return {
          title: name,
          value: code,
        };
      });
    },
    anonymousUserToken() {
      return this.$store.getters.anonymousUserToken;
    },
  },
  async mounted() {
    const lang = this.$route.query.language;

    const language = parseQueryParam(lang);

    this.getPreselectedCompanyData();

    if (language) {
      this.$store.dispatch('changeLanguage', language);
    }
    this.$store.dispatch('sendAnalyticsEvent', {
      event: 'app_request-invoice_page-view',
    });
  },
  methods: {
    async submitData() {
      this.form.loading = true;
      this.error = null;
      const paymentUuid = this.$route.params.paymentUuid;
      if (this.$refs.form.validate()) {
        try {
          await this.$store.dispatch('submitInvoiceRequest', {
            data: {
              payment_uuid: paymentUuid,
              name: this.form.name,
              business_name: this.form.businessName,
              business_code: this.form.businessCode,
              business_address: this.form.businessAddress,
              email: this.form.email,
              business_vat_code: this.form.enableVatCodeField
                ? this.form.vatCode
                : null,
              business_country_code: this.form.country,
              language: this.$i18n.locale(),
              anonymous_user_token: this.anonymousUserToken,
              save_request_invoice_details: this.form.saveRequestInvoiceDetails,
            },
          });
          this.showSuccessScreen = true;
        } catch (e) {
          if (e.response.data.message.includes('Invoice cannot be changed')) {
            this.error = this.$i18n.translate(
              'components.requestInvoice.error.cannotUpdateInvoice'
            );
          } else if (e.response.data.message.includes('VAT Code invalid')) {
            this.error = this.$i18n.translate(
              'components.requestInvoice.error.vatCodeInvalid'
            );
          } else {
            this.error = e.response.data.message;
          }
        }
      }

      this.form.loading = false;
    },
    goBack() {
      this.$router.go(-1);
    },
    fillCompanyData(company) {
      this.enterCompanyNameManually = false;
      this.dataFilled = true;

      this.form.businessName = company.title;
      this.form.businessCode = company.code;
      this.form.businessAddress = company.address;
      this.form.vatCode = company.pvmCode;
      this.form.enableVatCodeField = !!company.pvmCode;
    },
    resetDataForManualEntry() {
      this.enterCompanyNameManually = true;
      this.dataFilled = false;

      this.form.businessName = null;
      this.form.businessCode = null;
      this.form.businessAddress = null;
      this.form.vatCode = null;
      this.form.enableVatCodeField = false;
    },
    resetDataForAutomaticEntry() {
      this.enterCompanyNameManually = false;
      this.dataFilled = false;

      this.form.businessName = null;
      this.form.businessCode = null;
      this.form.businessAddress = null;
      this.form.vatCode = null;
      this.form.enableVatCodeField = false;
    },
    async getPreselectedCompanyData() {
      const anonymousUserToken = this.anonymousUserToken;
      try {
        const response = await this.axios.get(
          `/anonymous-user-legal-entity/${anonymousUserToken}`
        );
        const data = response.data;

        if (data) {
          this.form.businessName = data.business_name;
          this.form.businessCode = data.business_code;
          this.form.businessAddress = data.business_address;
          this.form.vatCode = data.business_vat_code;
          this.form.enableVatCodeField = !!data.business_vat_code;
          this.form.country = data.business_country_code;
          this.form.name = data.name;
          this.form.email = data.email;
          this.form.saveRequestInvoiceDetails = true;

          this.enterCompanyNameManually = true;
          this.dataFilled = true;
        }
      } catch (error) {
        console.error('Error fetching preselected company data:', error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.marginless ::v-deep {
  margin-top: 0;
  .v-input--selection-controls__input {
    width: 40px;
    margin: 0;
  }
}
</style>
